// src/features/localization/NamespacesSettings.tsx
// Admin escape hatch: CreateNamespace (products normally do this on startup),
// GetNamespace detail. Listing lives on the keys manager's left panel
// (NamespaceTree, ListNamespaces) — not duplicated here.

import { useState } from 'react';
import { useI18n, useLocalization } from '@react/hooks';
import { Alert, Button, Input, Textarea } from '@react/components';
import { Select } from '@/components/Select';
import { useScope } from '@/hooks/useScope';
import type { NamespaceScope, TranslationNamespace } from '@core/types/localization';
import { errMessage, makeEntity } from './helpers';

const SCOPES: NamespaceScope[] = ['NAMESPACE_SCOPE_PLATFORM', 'NAMESPACE_SCOPE_PRODUCT', 'NAMESPACE_SCOPE_TENANT'];

export function NamespacesSettings() {
  const { t } = useI18n();
  const scope = useScope();
  const store = useLocalization();

  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nsScope, setNsScope] = useState<NamespaceScope>('NAMESPACE_SCOPE_PRODUCT');
  const [productSlug, setProductSlug] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [lookupId, setLookupId] = useState('');
  const [lookupResult, setLookupResult] = useState<TranslationNamespace | null | 'none'>(null);
  const [lookupBusy, setLookupBusy] = useState(false);

  const create = async () => {
    if (!slug.trim() || !name.trim()) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await store.CreateNamespace({
        entity: makeEntity('', 'translation_namespace', scope),
        slug: slug.trim(),
        name: name.trim(),
        description: description || undefined,
        scope: nsScope,
        product_slug: productSlug || undefined,
      });
      setSuccess(t('localization.namespaces.createSuccess'));
      setSlug('');
      setName('');
      setDescription('');
      setProductSlug('');
    } catch (e) {
      setError(errMessage(e, t('localization.namespaces.saveError')));
    } finally {
      setBusy(false);
    }
  };

  const lookup = async () => {
    if (!lookupId.trim()) return;
    setLookupBusy(true);
    setLookupResult(null);
    try {
      const res = await store.GetNamespace(lookupId.trim());
      setLookupResult(res.namespace ?? 'none');
    } catch {
      setLookupResult('none');
    } finally {
      setLookupBusy(false);
    }
  };

  return (
    <div className="max-w-xl space-y-6">
      <p className="text-sm text-muted-foreground">{t('localization.namespaces.hint')}</p>

      {error && <Alert variant="error">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <div className="space-y-3 rounded-md border border-border p-3">
        <span className="block text-sm font-medium text-foreground">{t('localization.namespaces.newNamespace')}</span>
        <Input label={t('localization.namespaces.slug')} value={slug} onChange={(e) => setSlug(e.target.value)} required />
        <Input label={t('common.name')} value={name} onChange={(e) => setName(e.target.value)} required />
        <Textarea label={t('common.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        <div className="grid grid-cols-2 gap-2">
          <Select
            label={t('localization.namespaces.scope')}
            value={nsScope}
            onChange={(e) => setNsScope(e.target.value as NamespaceScope)}
            options={SCOPES.map((s) => ({ value: s, label: t(`localization.namespaceScope.${s.replace('NAMESPACE_SCOPE_', '').toLowerCase()}`) }))}
          />
          <Input label={t('localization.namespaces.productSlug')} value={productSlug} onChange={(e) => setProductSlug(e.target.value)} />
        </div>
        <Button size="sm" onClick={create} isLoading={busy} disabled={!slug.trim() || !name.trim()}>{t('common.create')}</Button>
      </div>

      <div className="space-y-2 rounded-md border border-border p-3">
        <span className="block text-sm font-medium text-foreground">{t('localization.namespaces.lookupTitle')}</span>
        <div className="flex items-end gap-2">
          <Input label={t('localization.namespaces.idOrSlug')} value={lookupId} onChange={(e) => setLookupId(e.target.value)} fullWidth={false} className="w-64" />
          <Button size="sm" variant="secondary" onClick={lookup} isLoading={lookupBusy} disabled={!lookupId.trim()}>{t('form.policies.lookup')}</Button>
        </div>
        {lookupResult === 'none' && <p className="text-sm text-muted-foreground">{t('form.policies.lookupNone')}</p>}
        {lookupResult && lookupResult !== 'none' && (
          <p className="text-sm text-foreground">{lookupResult.name} — {lookupResult.description}</p>
        )}
      </div>
    </div>
  );
}
